import type { BrowserBroadcast } from '@adapters/broadcast/browserBroadcast';
import { RedirectToExternalBankPage } from '@adapters/navigation/page';
import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { Logger } from '@core/model/domain/logger';
import { Option } from '@core/model/framework/Result/Option';
import { GetWebauthUrl } from '@core/usecase/getWebauthUrl';
import { browserBroadcastToken, loggerToken } from '@di/angularProviderMapper';
import { ConnectionFormItemDto } from '@modules/process/pages/login/services/dtos/loginDtos';
import { TrackerService } from '@shared/services/tracker.service';
import { firstValueFrom } from 'rxjs';
import { Buffer } from 'buffer';
import { InitInput, RedirectToExternalBankVM } from './redirect-to-external-bank.viewmodel';

export type RedirectExternalPageSelectedBank = {
  name: string;
  logo: Option<string>;
  uid: string;
};

@Component({
  selector: 'app-redirect-to-external-bank',
  templateUrl: './redirect-to-external-bank.component.html',
})
export class RedirectToExternalBankComponent implements OnInit {
  selectedBank: RedirectExternalPageSelectedBank | undefined;
  private redirectToExternalBankVM: RedirectToExternalBankVM;

  constructor(
    private activatedRoute: ActivatedRoute,
    private getWebauthUrl: GetWebauthUrl,
    private tracker: TrackerService,
    @Inject(browserBroadcastToken) private broadcastService: BrowserBroadcast,
    @Inject(loggerToken) private loggerService: Logger
  ) {
    this.redirectToExternalBankVM = new RedirectToExternalBankVM(
      this.getWebauthUrl,
      this.broadcastService,
      this.loggerService
    );
  }

  async ngOnInit(): Promise<void> {
    const { tagging, selectedBank, webauthForm, userToken } = (await firstValueFrom(
      this.activatedRoute.queryParams
    )) as Record<string, string>;

    if (!selectedBank) {
      throw new Error('RedirectToExternalBankComponent: selectedBank query param should be provided');
    }
    if (!webauthForm) {
      throw new Error('RedirectToExternalBankComponent: webauthForm query param should be provided');
    }
    if (!userToken) {
      throw new Error('RedirectToExternalBankComponent: userToken query param should be provided');
    }

    const formParsed = JSON.parse(Buffer.from(webauthForm, 'base64').toString()) as Option<
      ConnectionFormItemDto[]
    >;
    this.selectedBank = JSON.parse(
      Buffer.from(selectedBank, 'base64').toString()
    ) as RedirectExternalPageSelectedBank;

    const mappedQueryParams: InitInput = {
      tagging: tagging,
      userToken: userToken,
      selectedBankUid: this.selectedBank.uid,
      webauthForm: formParsed,
    };

    if (tagging != null) {
      await this.tracker.initializeTaggingTracker(tagging);
    } else {
      this.loggerService.logTrace('REDIRECT_TO_EXTERNAL_BANK: No tagging query param');
    }
    await this.redirectToExternalBankVM.onInit(mappedQueryParams);
  }

  get redirectToExternalBankPage(): RedirectToExternalBankPage {
    return this.redirectToExternalBankVM.redirectToExternalBankPage;
  }
}
